// redirectsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { redirectsAPI } from "api/redirectsApi";

// Async thunks
export const fetchRedirects = createAsyncThunk("redirects/fetchRedirects", async () => {
  return await redirectsAPI.getRedirects();
});

export const getRedirectById = createAsyncThunk(
  "redirects/getRedirectById",
  async (id, { rejectWithValue }) => {
    try {
      return await redirectsAPI.getRedirectById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRedirect = createAsyncThunk(
  "redirects/createRedirect",
  async (redirectData, { rejectWithValue }) => {
    try {
      return await redirectsAPI.createRedirect(redirectData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRedirect = createAsyncThunk(
  "redirects/updateRedirect",
  async (redirectData, { rejectWithValue }) => {
    try {
      return await redirectsAPI.updateRedirect(redirectData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRedirect = createAsyncThunk(
  "redirects/deleteRedirect",
  async (id, { rejectWithValue }) => {
    try {
      await redirectsAPI.deleteRedirect(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const redirectsSlice = createSlice({
  name: "redirects",
  initialState: {
    items: [],
    currentRedirect: null,
    status: "idle",
    error: null,
    loading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentRedirect: (state) => {
      state.currentRedirect = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch redirects
      .addCase(fetchRedirects.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(fetchRedirects.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchRedirects.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.error.message;
      })

      // Get redirect by ID
      .addCase(getRedirectById.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.currentRedirect = null;
      })
      .addCase(getRedirectById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.currentRedirect = action.payload.data;
      })
      .addCase(getRedirectById.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload;
      })

      // Create redirect
      .addCase(createRedirect.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createRedirect.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items.push(action.payload);
      })
      .addCase(createRedirect.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update redirect
      .addCase(updateRedirect.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateRedirect.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      })
      .addCase(updateRedirect.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete redirect
      .addCase(deleteRedirect.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteRedirect.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteRedirect.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentRedirect } = redirectsSlice.actions;
export default redirectsSlice.reducer;
